// 'use client' は付けない: コールバック引数(getPayload/onExit)を受け取るフックに付けると
// ts71007（Props must be serializable）になるため。利用側のクライアントコンポーネントから import する。
import { useCallback, useEffect, useRef } from 'react';
import { clearPendingExit, loadPendingExit, savePendingExit } from './pendingExitStorage';

type UseExitEventParams<T> = {
  /** localStorage のキー。機能ごとに一意にする */
  storageKey: string;
  /** 送信する最新ペイロードを返す（呼ばれた瞬間の状態をスナップショット） */
  getPayload: () => T;
  /**
   * 実際の送信処理。計測手段（PostHog/GA4 dual-send 等）はここに委ねる。
   * @param meta.isRecovered 前回セッションの未送信を救出して送る場合 true
   */
  onExit: (payload: T, meta: { isRecovered: boolean }) => void;
  /** false の間は一切送らない（テスト/未ログイン制御用） */
  enabled?: boolean;
};

/**
 * 「離脱時に確実にイベントを送る」汎用フック。送信手段に依存しない。
 *
 * 仕組み:
 * 1. 呼び出し側が操作のたびに `persist()` を呼ぶ → 最新ペイロードを localStorage に退避
 * 2. 離脱時（pagehide / SPA アンマウント）に1回だけ `onExit(..., {isRecovered:false})` → 退避を削除
 * 3. 次回マウント時に未送信の退避が残っていれば `onExit(..., {isRecovered:true})` で救出 → 削除
 *
 * 設計上の注意:
 * - 離脱は `pagehide`（タブ閉じ/リロード/外部遷移）と unmount（SPA 遷移）で検知する。
 *   `visibilitychange(hidden)` はタブ切替でも発火し途中確定→取りこぼすため使わない。
 * - unmount 送信は React StrictMode の mount→unmount→remount でも走るため、
 *   `queueMicrotask` + `mountedRef` で「再マウントされない＝本当の離脱」時のみ送る。
 * - 一発送信が落ちる端末（モバイル OS kill 等）は (3) の救出で取りこぼしを緩和する。
 *
 * @returns persist 最新ペイロードを localStorage に退避する関数（操作のたびに呼ぶ）
 * @returns cancel 意図的な遷移（リロード等）の直前に呼ぶと、以降の離脱送信と次回マウントの救出送信を止める
 */
export function useExitEvent<T>({ storageKey, getPayload, onExit, enabled = true }: UseExitEventParams<T>): {
  persist: () => void;
  cancel: () => void;
} {
  const getPayloadRef = useRef(getPayload);
  const onExitRef = useRef(onExit);
  const sentRef = useRef(false);
  const mountedRef = useRef(false);

  // 最新の関数を ref に保持（effect/コールバックからは常に最新を参照する）
  useEffect(() => {
    getPayloadRef.current = getPayload;
    onExitRef.current = onExit;
  });

  const persist = useCallback(() => {
    if (!enabled || sentRef.current) return;
    savePendingExit(storageKey, getPayloadRef.current());
  }, [enabled, storageKey]);

  const sendExit = useCallback(() => {
    if (sentRef.current || !enabled) return;
    sentRef.current = true;
    onExitRef.current(getPayloadRef.current(), { isRecovered: false });
    clearPendingExit(storageKey);
  }, [enabled, storageKey]);

  // 意図的な遷移（例: 「最初の状態に戻す」のリロード）は離脱ではないため、
  // 遷移直前にこれを呼ぶと pagehide/unmount での送信も次回マウントの救出も止める。
  const cancel = useCallback(() => {
    sentRef.current = true;
    clearPendingExit(storageKey);
  }, [storageKey]);

  // マウント時: 前回セッションの未送信を救出
  useEffect(() => {
    if (!enabled) return;
    const pending = loadPendingExit<T>(storageKey);
    if (pending !== null) {
      onExitRef.current(pending, { isRecovered: true });
      clearPendingExit(storageKey);
    }
  }, [enabled, storageKey]);

  // 離脱検知: pagehide + unmount（StrictMode セーフ）
  useEffect(() => {
    if (!enabled) return;
    mountedRef.current = true;
    const handlePageHide = () => sendExit();
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('pagehide', handlePageHide);
      queueMicrotask(() => {
        if (!mountedRef.current) sendExit();
      });
    };
  }, [enabled, sendExit]);

  return { persist, cancel };
}
